import { useChatData, useChatsData } from "@/data/use-chats-data";
import { MessageStage } from "@/data/use-messages-data";
import { useUserLocalSettings } from "@/hooks/use-user-local-settings";
import { noop } from "lodash";
import { createContext, useContext, useEffect, useState } from "react";
import { useKitData } from "../../data/use-kits-data";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { WithExpertSelection } from "../Expert/selection/ExpertSelectionContext";

export interface Capabilities {
  webSearch: boolean;
  projectFiles: boolean;
  expertFiles: boolean;
  imageGeneration: boolean;
}

type AssistMessageStage = MessageStage | "uploading";

interface AssistContextType {
  activeChatUuid: string | null;
  setActiveChatUuid: (uuid: string | null) => void;
  forwardChatUuid: string | null;
  setForwardChatUuid: (uuid: string | null) => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  isExpandable: boolean;
  activeChat: ReturnType<typeof useChatsData>["chats"][number] | null;
  forwardChat: ReturnType<typeof useChatsData>["chats"][number] | null;
  capabilities: Capabilities;
  setCapabilities: (capabilities: Capabilities) => void;
  selectedSections: string[];
  setSelectedSections: (sections: string[]) => void;
  toggleSectionSelection: (sectionKey: string) => void;
  kit: ReturnType<typeof useKitData>["kit"] | null;
  modelProfileUuid: string | undefined;
  setModelProfileUuid: (modelProfileUuid: string | undefined) => void;
  userPrompt: string;
  setUserPrompt: (prompt: string) => void;
  lastUserPrompt: string;
  messageStage: AssistMessageStage;
  setMessageStage: (stage: AssistMessageStage) => void;
}

const AssistContext = createContext<AssistContextType | undefined>(undefined);

export const useAssistContext = () => {
  const context = useContext(AssistContext);
  if (!context) {
    throw new Error("useAssistContext must be used within an AssistProvider");
  }
  return context;
};

interface AssistProviderProps {
  children: React.ReactNode;
}

export const AssistProvider: React.FC<AssistProviderProps> = ({ children }) => {
  const [activeChatUuid, setActiveChatUuid] = useState<string | null>(null);
  const [forwardChatUuid, setForwardChatUuid] = useState<string | null>(null);
  let [isExpanded, setIsExpanded] = useState(false);
  const [capabilities, setCapabilities] = useState<Capabilities>({
    webSearch: true,
    projectFiles: true,
    expertFiles: true,
    imageGeneration: false,
  });
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const { modelProfileUuid, setModelProfileUuid } = useUserLocalSettings();
  const [userPrompt, setUserPrompt] = useState<string>("");
  const [lastUserPrompt, setLastUserPrompt] = useState<string>("");
  const [messageStage, setMessageStage] = useState<AssistMessageStage>("idle");

  const toggleSectionSelection = (sectionKey: string) => {
    setSelectedSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((key) => key !== sectionKey)
        : [...prev, sectionKey],
    );
  };

  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { kit } = useKitData(groupUuid, kitUuid);
  const { chat: activeChat } = useChatData(groupUuid, kitUuid, activeChatUuid);
  const { chat: forwardChat } = useChatData(
    groupUuid,
    kitUuid,
    forwardChatUuid,
  );
  const kitType = kit?.type;
  const isExpandable = kitType !== "conversation";

  // Check for chat query parameter and set active chat UUID
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatParam = urlParams.get("chat");

    if (chatParam) {
      setActiveChatUuid(chatParam);

      // Remove the chat parameter from the URL
      urlParams.delete("chat");
      const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  useEffect(() => {
    setActiveChatUuid(null);
    setForwardChatUuid(null);
  }, [kitUuid]);

  useEffect(() => {
    if (userPrompt) {
      setLastUserPrompt(userPrompt);
    }
  }, [userPrompt]);

  // Override expansion behavior for conversation kits
  if (kitType === "conversation") {
    isExpanded = true;
    setIsExpanded = noop;
  }

  const value: AssistContextType = {
    activeChatUuid,
    setActiveChatUuid: (uuid) => {
      setActiveChatUuid(uuid);
      if (uuid) {
        setForwardChatUuid(null);
      }
    },
    forwardChatUuid,
    setForwardChatUuid,
    isExpanded,
    setIsExpanded,
    isExpandable,
    capabilities,
    setCapabilities,
    selectedSections,
    setSelectedSections,
    toggleSectionSelection,
    kit,
    modelProfileUuid,
    setModelProfileUuid,
    userPrompt,
    setUserPrompt,
    lastUserPrompt,
    messageStage,
    setMessageStage,
    activeChat,
    forwardChat,
  };

  if (!groupUuid) {
    return (
      <AssistContext.Provider value={value}>{children}</AssistContext.Provider>
    );
  }

  return (
    <WithExpertSelection groupUuid={groupUuid} kitUuid={kitUuid}>
      <AssistContext.Provider value={value}>{children}</AssistContext.Provider>
    </WithExpertSelection>
  );
};
